import { appLocalStorage } from './storage';
import config from './config';

// Current-session metadata only. A saved slider vote must also update cards
// for the same event that were buffered before the write completed.
const opinions = new Map();
const listeners = new Set();
let owner, revision = 0, clearedRevision = 0;
const positive = value => Number.isSafeInteger(Number(value)) && Number(value) > 0;
const token = () => appLocalStorage.getItem(config.token);
const synchronize = () => {
  const current = token();
  if (owner !== current) { owner = current; opinions.clear(); clearedRevision = ++revision; }
  return current;
};
export const opinionRequestContext = () => ({ token: synchronize(), revision: ++revision });
export const readViewerOpinion = storyId => {
  if (!synchronize()) return undefined;
  return opinions.get(Number(storyId))?.value;
};
export const subscribeViewerOpinions = notify => {
  listeners.add(notify);
  return () => listeners.delete(notify);
};

export function observeViewerOpinion(key, data, response, context) {
  if (!response?.success || !context.token || synchronize() !== context.token) return;
  let changed = false;
  const write = (storyId, value, reactionId, version) => {
    if (!positive(storyId)) return;
    const id = Number(storyId), previous = opinions.get(id);
    // A slow feed response cannot undo a newer accepted vote/delete.
    if (previous && previous.revision > version) return;
    opinions.set(id, { value, reactionId: positive(reactionId) ? Number(reactionId) : previous?.reactionId, revision: version });
    changed = true;
  };
  if (key === 'reactionSaveVoting') {
    write(data?.story_id, true, response.id, ++revision);
  } else if (key === 'reactionClear') {
    const version = ++revision;
    clearedRevision = version;
    for (const [storyId] of opinions) write(storyId, false, null, version);
  } else if (key === 'reactionDeleteById') {
    const version = ++revision;
    for (const [storyId, opinion] of opinions) {
      if (opinion.reactionId === Number(data?.id)) write(storyId, false, null, version);
    }
  } else if (['reactionsLoad', 'reactionsLoadRefresh', 'reactionLoad', 'storyComment'].includes(key)) {
    if (context.revision < clearedRevision) return;
    for (const row of Array.isArray(response.list) ? response.list : [response]) {
      const storyId = row?.story?.id;
      if (typeof row?.viewer_has_opinion === 'boolean') {
        write(storyId, row.viewer_has_opinion, key === 'storyComment' ? row.id : null, context.revision);
      } else if (key === 'storyComment' && positive(storyId)) {
        write(storyId, positive(row.id), row.id, context.revision);
      }
    }
  }
  // Bound the page metadata without retaining text, photos or other accounts.
  while (opinions.size > 200) opinions.delete(opinions.keys().next().value);
  if (changed) listeners.forEach(notify => notify());
}

export function hasViewerOpinion(reaction, userId, observed) {
  if (!positive(userId) || !reaction) return false;
  // Public previews provide a safe fallback while old API instances roll out.
  if (Number(reaction.user?.id) === Number(userId)
    || (!reaction.quote?.unavailable && Number(reaction.quote?.user?.id) === Number(userId))) return true;
  return observed ?? reaction.viewer_has_opinion === true;
}
